import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/dashboard/shared";
import { getAPIBaseURL, getAdminHeaders } from "@/lib/auth";

type SignupBonus = { diamonds: number; updated_at: string | null };

const ENDPOINT = "/api/v1/admin/signup-bonus";
const MAX = 100000;

export function SignupBonusPage() {
  const queryClient = useQueryClient();
  const current = useQuery({
    queryKey: ["signup-bonus"],
    queryFn: async () => {
      const response = await fetch(`${getAPIBaseURL()}${ENDPOINT}`, { headers: getAdminHeaders() });
      if (!response.ok) throw new Error("Failed to load the signup bonus");
      return response.json() as Promise<SignupBonus>;
    },
  });

  const [value, setValue] = React.useState("");
  React.useEffect(() => {
    if (current.data) setValue(String(current.data.diamonds));
  }, [current.data]);

  const amount = Number(value);
  const valid = value.trim() !== "" && Number.isInteger(amount) && amount >= 0 && amount <= MAX;

  const save = useMutation({
    mutationFn: async (diamonds: number) => {
      const response = await fetch(`${getAPIBaseURL()}${ENDPOINT}`, {
        method: "PUT",
        headers: { ...getAdminHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ diamonds }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error ?? "Save failed");
      return payload as SignupBonus;
    },
    onSuccess: () => {
      toast.success("Signup bonus saved. New accounts will receive it from now on.");
      queryClient.invalidateQueries({ queryKey: ["signup-bonus"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="space-y-6">
      <PageHeader title="Signup bonus" description="Diamonds every new account receives when it is created. Change it at any time; no code change is needed." />

      <Card className="rounded-2xl">
        <CardContent className="flex flex-col gap-2 p-5 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Current bonus</p>
            {current.isLoading ? (
              <Skeleton className="mt-2 h-5 w-40" />
            ) : current.isError ? (
              <p className="mt-1 text-sm text-destructive">The current bonus could not be loaded.</p>
            ) : (
              <p className="mt-1 text-sm">{current.data?.diamonds ?? 0} diamonds per new account</p>
            )}
          </div>
          {current.data?.updated_at && (
            <p className="text-xs text-muted-foreground">Last changed {new Date(current.data.updated_at).toLocaleString("en-GB")}</p>
          )}
        </CardContent>
      </Card>

      {!current.isError && (
        <Card className="rounded-2xl">
          <CardContent className="space-y-6 p-6">
            <div className="space-y-2 md:max-w-sm">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Diamonds for each new account</Label>
              <Input inputMode="numeric" value={value} onChange={(e) => setValue(e.target.value.replace(/\D/g, ""))} placeholder="0" />
            </div>

            <div className="rounded-xl bg-muted/40 p-4 text-sm text-muted-foreground">
              Use 0 to give no bonus. The amount applies to accounts created after you save; existing accounts are not changed.
            </div>

            {value !== "" && !valid && (
              <p className="text-sm text-destructive">Enter a whole number between 0 and {MAX}.</p>
            )}

            <Button className="rounded-xl" disabled={!valid || save.isPending} onClick={() => save.mutate(amount)}>
              {save.isPending ? "Saving…" : "Save signup bonus"}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
